import { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Paper } from '@mui/material';

// SeatsIO
import { SeatsioEventManager } from '@seatsio/seatsio-react';

// Components
import { LoadingState, ErrorState } from 'components/ui/states';

// Utility
import fetchExpresso from 'utility/fetchExpresso';

const MODE_LABELS = {
    createOrder: 'Create Order',
    editOrder: 'Edit Order',
    filterSections: 'Sections',
    manageCategories: 'Categories',
    manageChannels: 'Channels',
    manageForSaleConfig: 'Edit Seat Availability',
    manageObjectStatuses: 'Bookings',
    manageTableBooking: 'Table Bookings',
    select: 'Custom',
    static: 'Seat Map',
};

/**
 * Seating chart wrapper that loads SeatsIO keys for an event and renders the
 * SeatsIO event manager with optional order editing capabilities.
 *
 * @param {object} props
 * @param {string|number} props.eventId - Event identifier used to load SeatsIO keys.
 * @param {string[]} [props.modes] - SeatsIO modes to allow, first value is default.
 * @param {Array} [props.order] - Order data passed when editing an order.
 * @param {object} [props.pricing] - Pricing data passed when editing an order.
 * @param {Array} [props.channelKeys] - SeatsIO channels configuration.
 * @param {string|object} [props.session] - SeatsIO session configuration.
 * @param {function} [props.onObjectSelected] - SeatsIO object selected callback.
 * @param {function} [props.onObjectDeselected] - SeatsIO object deselected callback.
 * @param {function} [props.onSessionInitialized] - SeatsIO session initialized callback.
 * @param {function} [props.onSessionExpired] - SeatsIO session expired callback.
 * @param {import('react').Ref} [props.chartRef] - Optional ref forwarded to SeatsIO.
 * @returns {JSX.Element}
 */
export default function SeatingChart({
    eventId,
    modes = ['static'],
    order = [],
    pricing,
    channelKeys,
    availableCategories,
    products,
    seatsPicked,
    onObjectSelected,
    onObjectDeselected,
    onSessionInitialized,
    onSessionExpired,
    chartRef,
}) {
    const [status, setStatus] = useState('loading');
    const [secretKey, setSecretKey] = useState(null);
    const [eventKey, setEventKey] = useState(null);
    const [mode, setMode] = useState(modes[0]);

    useEffect(() => {
        let isMounted = true;

        const loadSeatingData = async () => {
            try {
                setStatus('loading');

                const response = await fetchExpresso(`/apiv2/events/${eventId}`);

                if (response.status !== 200) {
                    throw new Error();
                }

                const data = await response.json();
                const { seats_private_key, seat_event_key } = data;

                if (seats_private_key && seat_event_key && isMounted) {
                    setSecretKey(seats_private_key);
                    setEventKey(seat_event_key);
                    setStatus('ready');
                } else {
                    throw new Error();
                }
            } catch (error) {
                if (isMounted) {
                    setStatus('error');
                }
            }
        };

        loadSeatingData();

        return () => {
            isMounted = false;
        };
    }, [eventId]);

    const handleObjectSelected = (obj, ticket) => {
        // Check if Seat or Table is already selected
        if ((obj.objectType === 'Seat' || obj.objectType === 'Table') && seatsPicked.find((s) => s.object.uuid === obj.uuid)) {
            return;
        }
        // Check if General Admission area is already selected
        // When seating chart is restored, the callback is run for every previously selected GA seat.
        // We only add a new seat if the selected count is higher than the current count to avoid duplicates being created.
        // Note: We may want to stop adding multiple recs per seat, and just keep one with a count of how many were selected.
        if (obj.objectType === 'GeneralAdmissionArea') {
            let numSelected = 0;
            let prevNumSelected = 0;

            if (ticket?.ticketType) {
                // Multi-level
                // Figure out how many selections for this GA Area + Product combo are in the cart
                numSelected = obj.selectionPerTicketType[ticket.ticketType];
                prevNumSelected = seatsPicked.reduce((acc, seat) => {
                    if (seat.productId === Number(ticket.ticketType) && seat.object.uuid === obj.uuid) {
                        return acc + 1;
                    } else {
                        return acc;
                    }
                }, 0);
            } else {
                // Simple pricing
                numSelected = obj.numSelected;
                prevNumSelected = seatsPicked.reduce((acc, seat) => seat.object.uuid === obj.uuid ? acc + 1 : acc, 0);
            }

            // If there are more selected GA seats in the cart, this means its a duplicate from SeatsIO re-rendering
            // This GA seat has already been selected
            if (numSelected <= prevNumSelected) {
                return;
            }
        }

        onObjectSelected(
            createAssignedSeat(obj, ticket, products)
        );
    }

    const handleObjectDeselected = (seat, ticket) => {
        const obj = createAssignedSeat(seat, ticket, products);
        onObjectDeselected(obj);
    }

    if (status === 'loading') {
        return (
            <Paper>
                <LoadingState />
            </Paper>
        );
    }

    if (status === 'error') {
        return (
            <Paper>
                <ErrorState message="Unable to load seating data" />
            </Paper>
        );
    }

    return (
        <Paper>
            {modes.length > 1 && (
                <Box mt={2}>
                    <Tabs
                        value={mode}
                        onChange={(_, value) => setMode(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {modes.map((modeValue) => (
                            <Tab
                                key={modeValue}
                                value={modeValue}
                                label={MODE_LABELS[modeValue] || modeValue}
                            />
                        ))}
                    </Tabs>
                </Box>
            )}
            <Box height={'500px'}>
                {secretKey && eventKey ? (
                    <SeatsioEventManager
                        // Managed by SeatingChart:
                        secretKey={secretKey}
                        event={eventKey}
                        mode={mode}
                        order={mode === 'editOrder' ? order : undefined}
                        pricing={['editOrder', 'createOrder'].includes(mode) ? pricing : undefined}
                        session={mode === 'createOrder' ? "continue" : undefined}
                        region="na"
                        // Passed through by parent component:
                        ref={chartRef}
                        availableCategories={availableCategories}
                        channels={['editOrder', 'createOrder'].includes(mode) && channelKeys.length > 0 ? channelKeys : undefined}
                        onObjectSelected={mode === 'createOrder' ? handleObjectSelected : undefined}
                        onObjectDeselected={mode === 'createOrder' ? handleObjectDeselected : undefined}
                        onSessionInitialized={mode === 'createOrder' ? onSessionInitialized : undefined}
                        onHoldTokenExpired={mode === 'createOrder' ? onSessionExpired : undefined}

                    />
                ) : (
                    <ErrorState message="Seating data is missing." />
                )}
            </Box>
        </Paper>
    );
}


// Utility Functions

function createAssignedSeat(obj, ticket, products) {
    const ticketType = ticket?.ticketType;

    const product = findProductIdForSeat(obj, ticketType, products); // Find the productId based on the selected ticket type (using product id)

    return (
        {
            productId: product.id,
            productName: product.name,
            price: getPrice(obj),
            object: {
                key: obj.id,
                type: obj.objectType,
                uuid: obj.uuid,
            },
            channel: obj.channel ? ({
                key: obj.channel.key,
                label: obj.channel.name
            }) : null,
            category: {
                key: obj.category?.key,
                label: obj.category?.label,
            },
            section: {
                type: getSectionType(obj),
                label: obj.labels?.section || null,
            },
            row: {
                type: getRowType(obj),
                label: obj.labels?.parent || null,
            },
            seat: {
                type: getSeatType(obj),
                label: obj.labels?.own || null,
            },
            seats: obj.seats?.map(o => ({
                object: {
                    key: o.id,
                    type: o.objectType,
                    uuid: o.uuid,
                },
                section: {
                    type: getSectionType(o),
                    label: o.labels?.section,
                },
                row: {
                    type: getRowType(o),
                    label: o.labels?.parent,
                },
                seat: {
                    type: getSeatType(o),
                    label: o.labels?.own,
                },
            })) || [],
        }
    )
}

function getPrice(obj) {
    if (obj.selectedTicketType && obj.pricing?.ticketTypes) {
        const selectedType = obj.pricing.ticketTypes.find(
            (tt) => tt.ticketType === obj.selectedTicketType,
        );
        return selectedType?.price ?? 0;
    }

    return obj.pricing?.price ?? obj.price ?? 0;
}

function getSectionType(obj) {
    return obj.labels?.section ? 'section' : null;
}

function getRowType(obj) {
    if (obj.parent?.type) {
        return String(obj.parent?.type).toLowerCase();
    } else {
        return null
    }
}

function getSeatType(obj) {
    if (obj.objectType === 'GeneralAdmissionArea') {
        return 'ga'
    } else if (obj.objectType) {
        return String(obj.objectType).toLowerCase();
    } else {
        return null;
    }
}

// Helper to find product for a seat selection
// For multi-level pricing (multiple ticket types), ticket.ticketType contains the product ID
// For simple pricing (single ticket type per category), we look up by category key
function findProductIdForSeat(seat, ticket, products) {
    // Multi-level: ticket contains the product ID
    if (ticket) {
        return products.find((p) => String(p.id) === ticket);
    }

    // Simple pricing: find the product that has this category in its seatPricing
    const categoryKey = String(seat.category.key);
    return products.find(
        (p) =>
            p.type === 3 &&
            p.seatPricing?.some((sp) => String(sp.seat_category_id) === categoryKey),
    );
}
